import React, { useCallback, useEffect, useState } from "react";
import { api } from "./api";

// The Test Outbox (build plan §8A.4). Test environment only: the nav item and
// the routes behind it do not exist in the live app. Every non-sign-in email
// the test app would have sent is kept here instead, exactly as it would have
// gone. The body is shown in a sandboxed iframe with no scripts.

interface Summary {
  noteKey: string;
  personKey: string;
  authorEmail: string;
  createdUtc: string;
  to: string[];
  cc: string[];
  subject: string;
  category: string;
  wouldHaveSentAtUtc: string;
}
interface Entry extends Summary {
  html: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  documentRequest: "Document request",
  parentNotification: "Parent notification",
  staffNotice: "Staff notice",
  digest: "Digest",
  report: "Report",
  insurance: "Insurance notice",
};

function eastern(iso: string): string {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

function Detail({ noteKey, onBack }: { noteKey: string; onBack: () => void }) {
  const [entry, setEntry] = useState<Entry | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setEntry(null);
    setError(null);
    void api<{ entry: Entry }>("GET", `/api/test/outbox/${encodeURIComponent(noteKey)}`).then((r) =>
      r.ok ? setEntry(r.data.entry) : setError(r.data.error ?? "Couldn't load that message.")
    );
  }, [noteKey]);

  return (
    <div className="section-card" aria-live="polite">
      <button type="button" className="btn-secondary mb-3 lg:hidden" onClick={onBack}>
        ← All messages
      </button>
      {error && <p className="field-error" role="alert">{error}</p>}
      {!entry && !error && <p className="text-sm text-slate-600">Loading…</p>}
      {entry && (
        <>
          <h3 className="break-words text-base font-semibold">{entry.subject}</h3>
          <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-slate-500">To</dt>
            <dd className="break-all">{entry.to.join(", ")}</dd>
            {entry.cc.length > 0 && (
              <>
                <dt className="text-slate-500">Cc</dt>
                <dd className="break-all">{entry.cc.join(", ")}</dd>
              </>
            )}
            <dt className="text-slate-500">Kind</dt>
            <dd>{CATEGORY_LABELS[entry.category] ?? entry.category}</dd>
            <dt className="text-slate-500">Would have sent</dt>
            <dd>{eastern(entry.wouldHaveSentAtUtc)}</dd>
            {entry.personKey && (
              <>
                <dt className="text-slate-500">About</dt>
                <dd className="font-mono">{entry.personKey}</dd>
              </>
            )}
            <dt className="text-slate-500">By</dt>
            <dd className="break-all">{entry.authorEmail}</dd>
          </dl>
          {/* sandbox="" with no allow-* tokens: no scripts, no forms, no navigation. */}
          <iframe
            title="Message body"
            sandbox=""
            srcDoc={entry.html}
            className="mt-4 h-96 w-full rounded-md border border-slate-200 bg-white"
          />
        </>
      )}
    </div>
  );
}

export function TestOutbox() {
  const [entries, setEntries] = useState<Summary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await api<{ entries: Summary[] }>("GET", "/api/test/outbox");
    if (r.ok) {
      setEntries(r.data.entries);
      setError(null);
    } else setError(r.data.error ?? "Couldn't load the Test Outbox.");
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function sendSample() {
    setBusy(true);
    setNotice(null);
    const r = await api<{ route: string }>("POST", "/api/test/outbox/sample", {});
    setBusy(false);
    if (r.ok) {
      setNotice("A sample parent notification was added below. Nothing was emailed.");
      await load();
    } else setError(r.data.error ?? "Couldn't create the sample.");
  }

  const list = (
    <div className="section-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Test Outbox</h2>
          <p className="mt-1 text-sm text-slate-600">
            Emails the test app would have sent, newest first. They were not sent. Sign-in emails still go out normally.
          </p>
        </div>
        <button type="button" className="btn-secondary" onClick={sendSample} disabled={busy}>
          {busy ? "Adding…" : "Add a sample notification"}
        </button>
      </div>
      {notice && <p className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900" role="status">{notice}</p>}
      {error && <p className="field-error" role="alert">{error}</p>}
      {entries === null && !error && <p className="mt-4 text-sm text-slate-600">Loading…</p>}
      {entries && entries.length === 0 && <p className="mt-4 text-sm text-slate-600">Nothing yet.</p>}
      {entries && entries.length > 0 && (
        <ul className="mt-4 divide-y divide-slate-200 border-y border-slate-200">
          {entries.map((e) => (
            <li key={e.noteKey}>
              <button
                type="button"
                onClick={() => setSelected(e.noteKey)}
                aria-current={selected === e.noteKey ? "true" : undefined}
                className={`block w-full px-2 py-3 text-left hover:bg-slate-50 ${selected === e.noteKey ? "bg-slate-100" : ""}`}
              >
                <span className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate text-sm font-medium">{e.subject}</span>
                  <span className="shrink-0 text-xs text-slate-500">{eastern(e.createdUtc)}</span>
                </span>
                <span className="mt-0.5 block truncate text-xs text-slate-600">
                  {CATEGORY_LABELS[e.category] ?? e.category} · to {e.to.join(", ")}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <div className="lg:grid lg:grid-cols-2 lg:gap-6">
      <div className={selected ? "hidden lg:block" : ""}>{list}</div>
      {selected ? (
        <Detail noteKey={selected} onBack={() => setSelected(null)} />
      ) : (
        <div className="hidden lg:block section-card text-sm text-slate-600">Choose a message to read it.</div>
      )}
    </div>
  );
}
